import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, Search, AlertTriangle, AlertCircle, Info, CheckCircle2, FileDown } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";

export default function AuditPage() {
  const location = useLocation();
  const [url, setUrl] = useState(location?.state?.url || "");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  const fetchHistory = () => api.get("/seo/audits").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { fetchHistory(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/seo/audit", { url, target_keyword: keyword || null });
      setResult(data);
      fetchHistory();
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      eyebrow="AI SEO Audit"
      title="Score any URL in seconds."
      subtitle="Drop a domain and get a full breakdown — issues, fixes, recommendations and quick wins."
      action={<ExportPdfButton />}
    >
      <form onSubmit={submit} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-6" data-testid="audit-form">
        <div className="grid md:grid-cols-[1fr_240px_180px] gap-3">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} type="url" required
                 placeholder="https://example.com"
                 className="h-12 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]"
                 data-testid="audit-url-input" />
          <Input value={keyword} onChange={(e) => setKeyword(e.target.value)}
                 placeholder="Target keyword (optional)"
                 className="h-12 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]"
                 data-testid="audit-keyword-input" />
          <Button type="submit" disabled={loading} className="h-12 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm font-medium" data-testid="audit-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Run audit</>}
          </Button>
        </div>
        {error && <div className="mt-4 text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="audit-error">{error}</div>}
      </form>

      {loading && <SkeletonAudit />}

      {result && <AuditResult data={result.result} url={result.url} />}

      {/* History */}
      {!result && history.length > 0 && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="audit-history">
          <div className="px-6 py-4 border-b border-zinc-800">
            <span className="label-eyebrow">Recent audits</span>
          </div>
          <ul className="divide-y divide-zinc-900">
            {history.slice(0, 8).map((h) => (
              <li key={h.id} className="px-6 py-4 flex items-center justify-between hover:bg-zinc-900/50 cursor-pointer transition-colors"
                  onClick={() => setResult(h)} data-testid={`audit-history-${h.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-200 truncate">{h.url}</div>
                  <div className="text-xs text-zinc-500 mt-1">{new Date(h.created_at).toLocaleString()}</div>
                </div>
                <Badge className="bg-zinc-900 border-zinc-700 text-zinc-200 rounded-sm font-mono">{h.result?.score ?? "-"}</Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashboardLayout>
  );
}

function SkeletonAudit() {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 h-64 animate-pulse" />
      <div className="lg:col-span-2 bg-zinc-950 border border-zinc-800 rounded-sm p-6 h-64 animate-pulse" />
      <div className="lg:col-span-3 bg-zinc-950 border border-zinc-800 rounded-sm p-6 h-96 animate-pulse" />
    </div>
  );
}

function AuditResult({ data, url }) {
  const score = data.score ?? 0;
  const grade = data.grade ?? "-";
  const cats = data.categories || [];
  const issues = data.issues || [];

  return (
    <div className="space-y-4 fade-in" data-testid="audit-result">
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
          <span className="label-eyebrow">Overall Score · {url}</span>
          <div className="flex items-baseline gap-2 mt-3">
            <span className="font-display text-7xl font-semibold tracking-tighter" data-testid="audit-score">{score}</span>
            <span className="text-zinc-500 text-sm">/100</span>
            <Badge className="ml-auto bg-[#0055FF]/15 text-[#3377FF] border-[#0055FF]/30 rounded-sm" data-testid="audit-grade">GRADE {grade}</Badge>
          </div>
          <div className="mt-6 h-2 bg-zinc-900 rounded-sm overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#EF4444] via-[#F59E0B] to-[#10B981]" style={{ width: `${score}%` }} />
          </div>
          <p className="text-sm text-zinc-400 mt-5 leading-relaxed">{data.summary}</p>
        </div>
        <div className="lg:col-span-2 bg-zinc-950 border border-zinc-800 rounded-sm p-6">
          <span className="label-eyebrow mb-4 block">Category Breakdown</span>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
            {cats.map((c) => (
              <div key={c.name} data-testid={`audit-cat-${c.name?.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-zinc-300">{c.name}</span>
                  <span className="font-mono text-zinc-400">{c.score}</span>
                </div>
                <div className="h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
                  <div className="h-full" style={{
                    width: `${c.score}%`,
                    background: c.status === "good" ? "#10B981" : c.status === "warning" ? "#F59E0B" : "#EF4444"
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-zinc-950 border border-zinc-800 rounded-sm">
          <div className="px-6 py-4 border-b border-zinc-800">
            <span className="label-eyebrow">Issues Found</span>
          </div>
          <div className="divide-y divide-zinc-900">
            {issues.map((it, i) => (
              <IssueRow key={i} {...it} />
            ))}
          </div>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm">
          <div className="px-6 py-4 border-b border-zinc-800">
            <span className="label-eyebrow">Quick Wins</span>
          </div>
          <ul className="p-6 space-y-3">
            {(data.quick_wins || []).map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" /> {q}
              </li>
            ))}
          </ul>
          <div className="px-6 pb-6">
            <span className="label-eyebrow block mb-3">Recommendations</span>
            <ol className="space-y-2.5 list-decimal list-inside text-sm text-zinc-400">
              {(data.recommendations || []).map((r, i) => <li key={i}>{r}</li>)}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

function IssueRow({ severity, title, description, fix }) {
  const map = {
    critical: { i: AlertCircle, c: "#EF4444" },
    warning: { i: AlertTriangle, c: "#F59E0B" },
    info: { i: Info, c: "#0055FF" },
  };
  const M = map[severity] || map.info;
  const Icon = M.i;
  return (
    <div className="px-6 py-4 hover:bg-zinc-900/50 transition-colors" data-testid={`issue-${severity}`}>
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: M.c }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-zinc-200">{title}</span>
            <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 text-[10px] uppercase">{severity}</Badge>
          </div>
          <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{description}</p>
          {fix && <p className="text-sm text-[#3377FF] mt-2 leading-relaxed"><span className="text-zinc-500">Fix → </span>{fix}</p>}
        </div>
      </div>
    </div>
  );
}

function ExportPdfButton() {
  const { active } = useProjects();
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    if (!active?.id) return;
    setDownloading(true);
    try {
      const res = await api.get(`/projects/${active.id}/audit/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rankpilot-audit-${(active.domain || "site").replace(/[^a-z0-9]/gi, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(false);
    }
  };
  if (!active?.id) return null;
  return (
    <Button onClick={download} disabled={downloading} variant="outline" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm h-10" data-testid="audit-export-pdf">
      {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
      Export PDF
    </Button>
  );
}

